import React from 'react';
import { Line } from 'react-chartjs-2';
import { Paper, Grid } from '@mui/material';
import { Translate, translate } from 'react-jhipster';
import { AUTHORITIES } from 'app/config/constants';

interface ValueChartProps {
  dates: string[];
  graphStatusFilter: string[];
  valueQuotedData: number[];
  valueBoughtData: number[];
  valueTransferredData: number[];
  valueSoldData: number[];
  valueKeptData: number[];
  valueCancelledData: number[];
  account: any;
  defaultCurrency: any[];
}

export const ValueChart: React.FC<ValueChartProps> = ({
  dates,
  graphStatusFilter,
  valueQuotedData,
  valueBoughtData,
  valueTransferredData,
  valueSoldData,
  valueKeptData,
  valueCancelledData,
  account,
  defaultCurrency,
}) => {
  const chartData = {
    labels: Object.values(dates),
    datasets: [
      ...(graphStatusFilter.includes('Quoted')
        ? [
            {
              label: translate('home.status.quoted'),
              data: valueQuotedData.length !== 0 && Object.values(valueQuotedData),
              borderColor: '#6366F1',
              backgroundColor: '#6366F1',
              borderWidth: 2,
              tension: 0.1,
            },
          ]
        : []),

      ...(graphStatusFilter.includes('Bought')
        ? [
            {
              label: translate('home.status.bought'),
              data: valueBoughtData.length !== 0 && Object.values(valueBoughtData),
              borderColor: '#F59E0B',
              backgroundColor: '#F59E0B',
              borderWidth: 2,
              tension: 0.1,
            },
          ]
        : []),

      ...(graphStatusFilter.includes('Transferred')
        ? [
            {
              label: translate('home.status.transferred'),
              data: valueTransferredData.length !== 0 && Object.values(valueTransferredData),
              borderColor: '#10B981',
              backgroundColor: '#10B981',
              borderWidth: 2,
              tension: 0.1,
            },
          ]
        : []),

      ...(graphStatusFilter.includes('Sold')
        ? [
            {
              label: translate('home.status.sold'),
              data: valueSoldData.length !== 0 && Object.values(valueSoldData),
              borderColor: '#1E3A8A',
              backgroundColor: '#1E3A8A',
              borderWidth: 2,
              tension: 0.1,
            },
          ]
        : []),

      ...(graphStatusFilter.includes('Kept')
        ? [
            {
              label: translate('home.status.kept'),
              data: valueKeptData.length !== 0 && Object.values(valueKeptData),
              borderColor: '#8B5CF6',
              backgroundColor: '#8B5CF6',
              borderWidth: 2,
              tension: 0.1,
            },
          ]
        : []),

      ...(graphStatusFilter.includes('Cancelled')
        ? [
            {
              label: translate('home.status.cancelled'),
              data: valueCancelledData.length !== 0 && Object.values(valueCancelledData),
              borderColor: '#EF4444',
              backgroundColor: '#EF4444',
              borderWidth: 2,
              tension: 0.1,
            },
          ]
        : []),
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      title: {
        display: false,
        text: 'Transaction Values',
      },
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        callbacks: {
          label(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(context.parsed.y);
            }
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback(value: any) {
            const numValue = typeof value === 'string' ? parseFloat(value) : value;

            if (numValue >= 1000000) {
              return (numValue / 1000000).toFixed(1) + 'M';
            } else if (numValue >= 1000) {
              return (numValue / 1000).toFixed(1) + 'K';
            }
            return numValue.toLocaleString();
          },
        },
      },
    },
  };

  const getCurrency = () => {
    return account?.authorities?.includes(AUTHORITIES.ADMIN) ||
      account?.authorities?.includes(AUTHORITIES.ALSHMOKH_ADMIN) ||
      account?.authorities?.includes(AUTHORITIES.ALSHMOKH_USER)
      ? 'KWD'
      : defaultCurrency[0]?.name;
  };

  return (
    <Grid item xs={12} sm={12} md={7}>
      <Paper className="chart-paper">
        <h5>
          <Translate contentKey="global.menu.report.chart.valueTitle" interpolate={{ currency: getCurrency() }} />
        </h5>
        &nbsp;
        <div>
          <Line data={chartData} height={420} width={120} options={chartOptions} />
        </div>
      </Paper>
    </Grid>
  );
};
